import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { getCycleFromCycleName, CYCLES, CYCLE_LABELS, CYCLE_COLORS } from '../utils/cycle.helpers';

function detectCycleFromClass(cls) {
  if (!cls) return null;
  return cls.level?.cycle?.name ? getCycleFromCycleName(cls.level.cycle.name) : null;
}

export default function Bulletins() {
  const navigate  = useNavigate();
  const [periods, setPeriods]   = useState([]);
  const [classes, setClasses]   = useState([]);
  const [periodId, setPeriodId] = useState('');
  const [classId, setClassId]   = useState('');
  const [cycle, setCycle]       = useState(null);
  const [students, setStudents] = useState([]);
  const [board, setBoard]       = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/catalog/periods').catch(() => []),
      api.get('/catalog/classes').catch(() => []),
    ]).then(([p, c]) => {
      setPeriods(p || []);
      setClasses(c || []);
      if (p?.[0]) setPeriodId(p[0].id);
      if (c?.[0]) { setClassId(c[0].id); setCycle(detectCycleFromClass(c[0])); }
    });
  }, []);

  // Charger les élèves de la classe
  useEffect(() => {
    if (!classId) return;
    // Essayer d'abord la route dédiée par classe
    api.get(`/students?classId=${classId}`).then((data) => {
      const list = Array.isArray(data) ? data : (data?.records || []);
      if (list.length > 0) {
        setStudents(list);
      } else {
        // Fallback : charger tous et filtrer
        api.get('/students').then((all) => {
          const inClass = (all || []).filter((s) =>
            (s.enrollments || []).some((en) => en.classId === classId)
          );
          setStudents(inClass);
        }).catch(() => {});
      }
    }).catch(() => {
      api.get('/students').then((all) => {
        const inClass = (all || []).filter((s) =>
          (s.enrollments || []).some((en) => en.classId === classId)
        );
        setStudents(inClass);
      }).catch(() => {});
    });
  }, [classId]);

  useEffect(() => {
    if (!periodId || !classId) return;
    setLoading(true); setError('');
    api.get(`/bulletins/class/${classId}?periodId=${periodId}`)
      .then(setBoard)
      .catch(() => setBoard(null))
      .finally(() => setLoading(false));
  }, [periodId, classId]);

  function onClassChange(e) {
    const id = e.target.value;
    const cls = classes.find((c) => c.id === id);
    setClassId(id);
    setCycle(detectCycleFromClass(cls));
    setBoard(null);
  }

  const cycleColor = CYCLE_COLORS[cycle] || 'var(--primary)';
  const cycleLabel = CYCLE_LABELS[cycle] || '';

  // Fusionner board et students — afficher tous les élèves même sans notes
  const displayStudents = (() => {
    if (board?.students?.length) return board.students;
    // Fallback : élèves sans notes
    return students.map((s) => ({
      student: s,
      moyenneGenerale: null,
      rang: null,
    }));
  })();

  if (cycle === CYCLES.PRESCOLAIRE) {
    return <BulletinsPrescolaire classId={classId} classes={classes} periods={periods} periodId={periodId}
      onClassChange={onClassChange} onPeriodChange={(e) => setPeriodId(e.target.value)} />;
  }

  if (cycle === CYCLES.ELEMENTAIRE) {
    return <BulletinsListe title="Bulletins Élémentaire" badge={{ bg: '#eff6ff', color: '#1d4ed8', label: '📚 Élémentaire' }}
      classId={classId} classes={classes} periods={periods} periodId={periodId}
      onClassChange={onClassChange} onPeriodChange={(e) => setPeriodId(e.target.value)}
      displayStudents={displayStudents} board={board} loading={loading} error={error}
      cycleParam="ELEMENTAIRE" navigate={navigate} />;
  }

  return (
    <BulletinsListe title="Conseil de classe" badge={cycleLabel ? { bg: cycleColor + '20', color: cycleColor, label: cycleLabel } : null}
      classId={classId} classes={classes} periods={periods} periodId={periodId}
      onClassChange={onClassChange} onPeriodChange={(e) => setPeriodId(e.target.value)}
      displayStudents={displayStudents} board={board} loading={loading} error={error}
      cycleParam={cycle || ''} navigate={navigate} />
  );
}

// ── Liste commune Post-Élémentaire / Secondaire / Élémentaire ──
function BulletinsListe({ title, badge, classId, classes, periods, periodId, onClassChange, onPeriodChange, displayStudents, board, loading, error, cycleParam, navigate }) {
  const sorted = [...displayStudents].sort((a, b) => (b.moyenneGenerale ?? -1) - (a.moyenneGenerale ?? -1));

  const mention = (moy) => {
    if (moy == null) return { l: '—', c: 'var(--muted)' };
    if (moy >= 16) return { l: 'Très bien',    c: '#16a34a' };
    if (moy >= 14) return { l: 'Bien',         c: '#22c55e' };
    if (moy >= 12) return { l: 'Assez bien',   c: '#84cc16' };
    if (moy >= 10) return { l: 'Passable',     c: '#f97316' };
    return              { l: 'Insuffisant',  c: '#dc2626' };
  };

  return (
    <div className="card">
      <div className="card-head">
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <h2>{title}</h2>
          {badge && <span className="badge" style={{ background: badge.bg, color: badge.color, fontSize: 11 }}>{badge.label}</span>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={classId} onChange={onClassChange} style={{ width: 160 }}>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={periodId} onChange={onPeriodChange} style={{ width: 140 }}>
            {periods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="form-error">{error}</div>}
        {loading ? (
          <div className="loading">Calcul des moyennes…</div>
        ) : displayStudents.length === 0 ? (
          <div className="empty">Aucun élève inscrit dans cette classe.</div>
        ) : (
          <>
            {board && (
              <div style={{ display: 'flex', gap: 20, padding: '8px 12px 12px', fontSize: 13, color: 'var(--muted)', flexWrap: 'wrap' }}>
                <span>Effectif <b className="mono" style={{ color: 'var(--ink)' }}>{board.effectif ?? displayStudents.length}</b></span>
                {board.moyenneClasse != null && <span>Moyenne de classe <b className="mono" style={{ color: 'var(--ink)' }}>{board.moyenneClasse}/20</b></span>}
                {board.tauxReussite != null && <span>Taux de réussite <b style={{ color: board.tauxReussite >= 50 ? 'var(--success)' : 'var(--danger)' }}>{board.tauxReussite}%</b></span>}
              </div>
            )}
            {!board && displayStudents.length > 0 && (
              <div style={{ padding: '8px 12px 10px', background: '#fffbeb', borderRadius: 6, marginBottom: 8, fontSize: 12, color: '#92400e' }}>
                ⚠ Aucune note saisie pour ce trimestre. Cliquez sur un élève pour voir ou préparer son bulletin.
              </div>
            )}
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 50 }}>Rang</th>
                    <th>Matricule</th>
                    <th>Élève</th>
                    <th className="num">Moyenne /20</th>
                    <th>Mention</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((s, i) => {
                    const moy = s.moyenneGenerale;
                    const { l, c } = mention(moy);
                    const stu = s.student || s;
                    return (
                      <tr key={stu.id} className="clickable"
                        onClick={() => navigate(`/bulletins/detail/${stu.id}?periodId=${periodId}&cycle=${cycleParam}`)}>
                        <td style={{ textAlign: 'center', fontWeight: 700 }}>
                          {moy != null ? (i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : s.rang ?? i + 1) : '—'}
                        </td>
                        <td className="mono">{stu.matricule}</td>
                        <td><b>{stu.lastName} {stu.firstName}</b></td>
                        <td className="num" style={{ fontFamily: 'monospace', fontWeight: 700, color: moy != null ? (moy >= 10 ? 'var(--success)' : 'var(--danger)') : 'var(--muted)' }}>
                          {moy ?? '—'}
                        </td>
                        <td><span style={{ fontSize: 12, color: c, fontWeight: 500 }}>{l}</span></td>
                        <td style={{ textAlign: 'right', color: 'var(--primary)', fontWeight: 600, fontSize: 12 }}>
                          Voir →
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Pré-Scolaire ──────────────────────────────────────
function BulletinsPrescolaire({ classId, classes, periods, periodId, onClassChange, onPeriodChange }) {
  const navigate = useNavigate();
  const [observations, setObservations] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!classId) return;
    setLoading(true);
    Promise.all([
      api.get('/students').then((all) => (all || []).filter((s) => (s.enrollments || []).some((en) => en.classId === classId))),
      api.get('/registry/observations-prescolaire').then((d) => {
        const all = (Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data }));
        return all.filter((o) => o.classId === classId && o.periodId === periodId);
      }).catch(() => []),
    ]).then(([stu, obs]) => { setStudents(stu); setObservations(obs); })
      .finally(() => setLoading(false));
  }, [classId, periodId]);

  return (
    <div className="card">
      <div className="card-head">
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <h2>Bulletins Pré-Scolaire</h2>
          <span className="badge" style={{ background: '#fef3c7', color: '#d97706', fontSize: 11 }}>🎨 Pré-Scolaire</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={classId} onChange={onClassChange} style={{ width: 150 }}>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={periodId} onChange={onPeriodChange} style={{ width: 140 }}>
            {periods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button className="btn btn-sm" onClick={() => navigate(`/evaluations/prescolaire/new?classId=${classId}`)}>
            + Observation
          </button>
        </div>
      </div>
      <div className="card-body">
        {loading ? <div className="loading">Chargement…</div> : students.length === 0 ? (
          <div className="empty">Aucun élève inscrit dans cette classe.</div>
        ) : (
          <>
            {observations.length === 0 && (
              <div style={{ padding: '8px 12px 10px', background: '#fffbeb', borderRadius: 6, marginBottom: 8, fontSize: 12, color: '#92400e' }}>
                ⚠ Aucune fiche d'observation pour ce trimestre. Cliquez sur un élève pour voir son bulletin vide.
              </div>
            )}
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Matricule</th><th>Élève</th><th>Sexe</th><th>Observation saisie</th><th></th></tr>
                </thead>
                <tbody>
                  {students.map((s) => {
                    const obs = observations.find((o) => o.observations?.[s.id]);
                    return (
                      <tr key={s.id} className="clickable"
                        onClick={() => obs ? navigate(`/bulletins/prescolaire/${obs.id}`) : navigate(`/bulletins/prescolaire-vide/${s.id}?periodId=${periodId}&classId=${classId}`)}>
                        <td className="mono">{s.matricule}</td>
                        <td><b>{s.lastName} {s.firstName}</b></td>
                        <td>{s.gender === 'F' ? '♀ Fille' : '♂ Garçon'}</td>
                        <td>{obs ? <span className="badge green">✓ Saisie</span> : <span className="badge gray">Non saisie</span>}</td>
                        <td style={{ textAlign: 'right', color: 'var(--primary)', fontWeight: 600, fontSize: 12 }}>Voir →</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
